import React, { useState, useRef, useEffect } from 'react';
import { HiX } from 'react-icons/hi';
import { FiMoreVertical, FiLogOut } from 'react-icons/fi';
import axios from 'axios';
import './Sidebar.css';
import ConversationList from './ConversationList';

const Sidebar = ({
  showSidebar,
  setShowSidebar,
  isMobile,
  // Conversation data props
  conversations,
  conversationId,
  loadConversation,
  editingConversationId,
  setEditingConversationId,
  editedConversationName,
  setEditedConversationName,
  handleSaveConversationName,
  setDeleteModalOpen,
  setConversationToDelete,
  loadingMoreConversations,
  loadMoreConversations,
  hasMoreConversations,
  onDeleteAllConversations,
  // Neue Prop für das Starten einer neuen Konversation
  startNewConversation,
  fetchConversations,
  setConversations,
  totalConversations
}) => {
  // User profile states
  const [userEmail, setUserEmail] = useState('benutzer@example.com');
  const [userName, setUserName] = useState('');
  const [userInitials, setUserInitials] = useState('B');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isAzureUser, setIsAzureUser] = useState(false);
  const profileMenuRef = useRef(null);

  // Hilfsfunktion zum Kürzen der E-Mail
  const truncateEmail = (email, maxLength = 18) => {
    if (!email || email.length <= maxLength) return email;

    const atIndex = email.indexOf('@');

    if (atIndex <= maxLength - 5) {
      // Wenn der Username kurz genug ist, kürze die Domain
      const username = email.substring(0, atIndex);
      return `${username}@...`;
    } else {
      // Sonst kürze den Username
      return `${email.substring(0, maxLength - 3)}...`;
    }
  };

  // Lade Benutzerinformationen beim Komponentenstart
  useEffect(() => {
    const loadUserInfo = () => {
      // Priority 1: Check for user data in localStorage (for Azure users)
      try {
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
          const userData = JSON.parse(storedUserData);

          setUserEmail(userData.email || 'benutzer@example.com');
          // Assume if userData is in localStorage, it's from the Azure flow.
          // The backend now controls what 'is_admin' means, so we don't set is_azure_user here.
          // We can infer it for UI purposes if needed, e.g., showing "Pro Plan".
          setIsAzureUser(true);

          let initials = 'B';
          let fullName = '';

          if (userData.name) {
            fullName = userData.name;
            const nameParts = userData.name.split(' ');
            if (nameParts.length >= 2) {
              initials = `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`;
            } else {
              initials = userData.name.charAt(0);
            }
          } else {
            // Fallback if name is missing from localStorage data
            fullName = userData.email ? userData.email.split('@')[0] : 'Benutzer';
            initials = fullName.charAt(0);
          }

          setUserName(fullName);
          setUserInitials(initials.toUpperCase());

          // Data loaded from localStorage, no need to call API.
          return;
        }
      } catch (error) {
        console.error('Error parsing user data from localStorage. Falling back to API.', error);
        // Clear corrupted data
        localStorage.removeItem('userData');
      }

      // Priority 2: Fallback to API call (for regular users or session resumption)
      const fetchUserInfoFromApi = async () => {
        try {
          const response = await axios.post('/user');
          const userData = response.data;

          if (userData) {
            setUserEmail(userData.email || 'benutzer@example.com');
            setIsAzureUser(userData.is_azure_user === true);

            let initials = 'B';
            let fullName = '';

            // This logic now primarily serves regular users, as Azure user's name is not in DB.
            if (userData.first_name && userData.last_name) {
              initials = `${userData.first_name.charAt(0)}${userData.last_name.charAt(0)}`;
              fullName = `${userData.first_name} ${userData.last_name}`;
            } else if (userData.name) {
              const nameParts = userData.name.split(' ');
              if (nameParts.length >= 2) {
                initials = `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`;
              } else {
                initials = userData.name.charAt(0);
              }
              fullName = userData.name;
            } else {
                // Fallback for Azure users on refresh (name is not in DB)
                fullName = userData.email ? userData.email.split('@')[0] : 'Benutzer';
                initials = fullName.charAt(0);
            }

            setUserName(fullName);
            setUserInitials(initials.toUpperCase());
          }
        } catch (error) {
          console.error('Fehler beim Laden der Benutzerinformationen via API:', error);
          if (error.response) {
            console.error('Error response data:', error.response.data);
          }
          // Set fallback user info
          setUserName('Benutzer');
          setUserEmail('benutzer@example.com');
          setUserInitials('B');
        }
      };

      fetchUserInfoFromApi();
    };

    loadUserInfo();
  }, []);

  // Handle logout
  const handleLogout = async (e) => {
    // Prevent event bubbling to parent elements
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      console.log('Logging out...');
      setShowProfileMenu(false); // Close menu immediately

      // Clear local storage before API call to ensure we're logged out even if the request fails
      localStorage.removeItem('token');
      localStorage.removeItem('userData'); // <-- Clear user data on logout

      // Make the logout API call
      await axios.post('/logout');

      // Check if the application is running in an iframe
      const isInIframe = window !== window.parent;

      if (isInIframe) {
        console.log('Application is in an iframe, redirecting to Azure login page');
        window.location.href = '/azure-login';
      } else {
        console.log('Normal environment, redirecting to standard login');
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Error during logout:', error);

      // Even on error, redirect appropriately based on iframe status
      const isInIframe = window !== window.parent;
      if (isInIframe) {
        window.location.href = '/azure-login';
      } else {
        window.location.href = '/login';
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isInIframe = window !== window.parent;
  const showLogoutButton = !isInIframe || !isAzureUser;

  return (
    <>
      {/* Overlay for mobile when sidebar is open */}
      {showSidebar && isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 backdrop-blur-sm transition-all duration-300"
          onClick={() => setShowSidebar(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`sidebar ${showSidebar ? '' : 'sidebar-hidden'} bg-gray-800 text-black pb-4 flex flex-col h-full lg:static z-40 ${isMobile ? 'border-r-0' : 'border-r border-gray-700'}`}>
        <div className="flex justify-between items-center mb-0 px-4 border-b border-gray-100" style={{ padding: '1rem 1rem' }}>
          <div className="flex items-center">
            <a href="/" className="flex items-center">
                <img
                    src="/images/logo/logo-bernie.svg"
                    alt="Bernie Logo"
                    className="h-6 mr-2"
                />
            </a>
          </div>
          <button
            className="text-gray-700 rounded p-1 hover:bg-gray-700 hover:text-white transition-colors"
            onClick={() => setShowSidebar(false)}
          >
            <HiX size={20} />
          </button>
        </div>

        {/* ConversationList - flex-grow will make it take available space */}
        <div className="flex-grow overflow-auto">
          <ConversationList
            conversations={conversations}
            conversationId={conversationId}
            loadConversation={loadConversation}
            editingConversationId={editingConversationId}
            setEditingConversationId={setEditingConversationId}
            editedConversationName={editedConversationName}
            setEditedConversationName={setEditedConversationName}
            handleSaveConversationName={handleSaveConversationName}
            setDeleteModalOpen={setDeleteModalOpen}
            setConversationToDelete={setConversationToDelete}
            loadingMoreConversations={loadingMoreConversations}
            loadMoreConversations={loadMoreConversations}
            hasMoreConversations={hasMoreConversations}
            onDeleteAllConversations={onDeleteAllConversations}
            onStartNewConversation={startNewConversation}
            fetchConversations={fetchConversations}
            setConversations={setConversations}
            totalConversations={totalConversations}
          />
        </div>

        {/* User Profile at the bottom */}
        <div className="mt-auto border-t border-gray-100 pt-3 px-4" ref={profileMenuRef}>
          <div className="relative">
            <div
              className="flex items-center p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
            >
              <div className="user-avatar mr-3">
                {userInitials}
              </div>
              <div className="flex-grow">
                <div className="text-sm font-medium text-gray-700 truncate">{userName}</div>
                <div className="text-xs text-gray-500 truncate" title={userEmail}>{truncateEmail(userEmail)}</div>
              </div>
              <FiMoreVertical className="text-gray-500" />
            </div>

            {/* Dropdown Menu */}
            {showProfileMenu && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-md shadow-lg py-1 border border-gray-100 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <div className="text-sm font-medium text-gray-700">{userName}</div>
                  <div className="text-xs text-gray-500 truncate" title={userEmail}>{truncateEmail(userEmail)}</div>
                  <div className="text-xs font-semibold text-blue-600 mt-1">{isAzureUser ? 'Pro Plan' : 'Free Plan'}</div>
                </div>
                <a
                  href="https://leadersoft.ai"
                  className="profile-menu-item"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Leadersoft
                </a>
                <a
                  href="https://quantonomics.com"
                  className="profile-menu-item"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Quantonomics
                </a>
                <a
                  href="https://boardscore.ch"
                  className="profile-menu-item"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Boardscore
                </a>
                <div className="border-t border-gray-100 my-1"></div>
                {showLogoutButton && (
                    <button
                      onClick={handleLogout}
                      className="profile-menu-item w-full text-left"
                    >
                      <FiLogOut className="mr-2" />
                      Abmelden
                    </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
